import { DotsVerticalIcon } from '@heroicons/react/outline';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import React, { useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import { compose } from 'recompose';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { slugify } from '@zedoc/text';
import {
  apiZedocPatientServiceSyncProgress,
  apiZedocSearchPatientRecords,
} from '../../../../common/api/zedoc';
import {
  PATIENT_CREATE_PATIENT,
  PATIENT_ATTACH_PARTICIPATION,
  PROJECT_ATTACH_PARTICIPATION,
  PROJECT_PROFILE_CREATE_PARTICIPATION,
  PROJECT_IMPORT_PARTICIPANTS,
  PROJECT_FORCE_SYNCHRONIZATION,
  ADMIN_UPDATE_PROJECT,
  PROJECT_EXPORT_PARTICIPANTS,
  PATIENT_MILESTONE_SEND_ACTIVITY_NOTIFICATIONS,
} from '../../../../common/permissions';
import { PTA_RESYNC_MAX_PATIENT_RECORDS } from '../../../../common/constants';
import Dropdown from '../../../../common/components/Dropdown';
import Menu from '../../../../common/components/Menu';
import Button from '../../../../components/Button';
import Stack from '../../../../common/components/primitives/Stack';
import { default as PatientRecordSelect } from '../../../../common/selectors/PatientRecord';
import ProjectTable from '../ProjectTable';
import ProjectInfo from '../ProjectTable/ProjectInfo';
import branding from '../../../../utils/branding';
import { notifyError } from '../../../../utils/notify';
import { openEditProjectDialog, openSurveyLinksDialog } from '../../actions';
import Card from '../../../../components/Card';
import { useRealm } from '../../../../utils/usePermission';
import ConnectedFilters from './ConnectedFilters';
import MenuItemTooltip from './MenuItemTooltip';
import ConfirmResendNotifications from './ConfirmResendNotifications';
import cleanFilters from '../../../../utils/cleanFilters';
import usePagination from '../../../../utils/usePagination';
import store from '../../store';
import { selectAllProjectMilestones } from '../../selectors';

const ProjectMilestones = compose()(
  ({
    onAddMilestone,
    onDownloadResponsesCSV,
    validate,
    onAddPatient,
    project,
    projectId,
    dashboardId,
    variables,
    onCsvUpload,
    csvInputSchemas,
    onForceSyncToProms,
    onResyncNotifications,
    onResyncCompleted,
    onCountResendCandidates,
    onResendNotifications,
  }) => {
    const { t } = useTranslation();
    const inputRef = useRef();
    const [resendSummary, setResendSummary] = useState(null);

    const { ready: progressReady } = useDDPSubscription(
      apiZedocPatientServiceSyncProgress.withParams({
        projectId,
      }),
    );

    const projectMilestones = useSelector(selectAllProjectMilestones);

    const rawFilters = useSelector(
      store.get(`dashboards.${dashboardId}.filters`),
    );

    const filters = useMemo(
      () => cleanFilters(rawFilters, { anyOf: true }),
      [rawFilters],
    );

    // On the very first load of a dashboard the store has no filters yet
    // (undefined) until ConnectedFilters seeds the default Enrolment-date
    // filter in an effect. Subscribing before that fires an UNFILTERED
    // searchPatientRecords over the whole project, immediately followed by
    // the filtered one - two heavy server queries for one page view. Hold
    // the subscription until the filters are seeded - but only ONCE: the
    // "Clear" button also resets the store value to undefined
    // (Filters/index.jsx calls onChange() with no args), and in that case
    // the user expects an unfiltered request, not a closed gate.
    const seededRef = useRef(false);
    if (rawFilters !== undefined) {
      seededRef.current = true;
    }
    const filtersReady = seededRef.current;

    const {
      isFetching: patientsFetching,
      items: patients,
      totalItems: nPatients,
      paginationKey,
      getPaginationProps,
    } = usePagination({
      selector: PatientRecordSelect,
      // Back to the first page whenever the filters change, see usePagination.
      resetPageKey: filters,
      getSubscription: (currentPage, resultsPerPage) =>
        // NOTE: must be null (not false) when gated - useDDPSubscription
        // accepts only nil or a plain object.
        projectId && filtersReady
          ? apiZedocSearchPatientRecords.withParams({
              projectId,
              filters,
              controlId: '$meta.id',
              pageIndex: currentPage - 1,
              resultsPerPage,
            })
          : null,
    });

    // Not settled until the filters are seeded as well: while the gate is
    // closed nothing is subscribed, and the actions below must not read that
    // as a loaded, empty patient list.
    const patientsReady = filtersReady && !patientsFetching;

    const [inputSchemaId, setInputSchemaId] = useState(null);
    const handleFileInputOnChange = useCallback(
      (event) => {
        const formData = new FormData();
        formData.append('patients', event.target.files[0]);
        // eslint-disable-next-line no-param-reassign
        event.target.value = '';
        onCsvUpload(projectId, inputSchemaId, formData);
      },
      [onCsvUpload, projectId, inputSchemaId],
    );
    const dispatch = useDispatch();
    const handleEditProject = useCallback(() => {
      dispatch(openEditProjectDialog({}));
    }, [dispatch]);

    const handleOpenSurveyLinksDialog = useCallback(() => {
      dispatch(openSurveyLinksDialog({}));
    }, [dispatch]);

    const createRealm = useRealm(
      [
        PATIENT_CREATE_PATIENT,
        PATIENT_ATTACH_PARTICIPATION,
        PROJECT_ATTACH_PARTICIPATION,
        PROJECT_PROFILE_CREATE_PARTICIPATION,
      ],
      {
        scope: project ? project.getDomains() : null,
      },
    );

    // const items = [
    //   {
    //     label: 'Alphabetically: A-Z',
    //   },
    //   {
    //     label: 'Alphabetically: Z-A',
    //   },
    //   {
    //     label: 'Date: Earlier- Latest',
    //   },
    //   {
    //     label: 'Date: Latest- Earlier',
    //   },
    // ];
    // Scoped "Sync to Patient Service" gating. The reactive nPatients count
    // uses the same cleaned filters as the replay, so it is the right operator
    // feedback, but it is only advisory - the server independently rejects a
    // cohort over PTA_RESYNC_MAX_PATIENT_RECORDS before touching a projection.
    //
    // NOTE: usePagination reports totalItems as 0 while the subscription is
    //       still loading, so "no matches" and "not loaded yet" are
    //       indistinguishable from the count alone. patientsReady has to be
    //       checked separately or the action flickers disabled-as-if-empty
    //       every time the filters change.
    const canForceSync = validate(PROJECT_FORCE_SYNCHRONIZATION, {
      relativeTo: project && project.getDomains(),
    });
    const isForceSyncOverLimit = nPatients > PTA_RESYNC_MAX_PATIENT_RECORDS;
    // A separate permission from the resync actions on purpose: being allowed to
    // repair sync state must not imply being allowed to message every patient in
    // the project. Same permission the single-activity reminder already uses.
    const canResendNotifications = validate(
      PATIENT_MILESTONE_SEND_ACTIVITY_NOTIFICATIONS,
      {
        relativeTo: project && project.getDomains(),
      },
    );
    const resendDisabledReason = canResendNotifications
      ? ''
      : t('tooltips:resendRemindersNotAllowed');
    const forceSyncDisabledReason = isForceSyncOverLimit
      ? t('tooltips:forceSyncOverLimit', {
          max: PTA_RESYNC_MAX_PATIENT_RECORDS.toLocaleString(),
        })
      : '';

    const confirmResendDialog = (
      <ConfirmResendNotifications
        visible={resendSummary !== null}
        count={resendSummary ? resendSummary.count : 0}
        patients={resendSummary ? resendSummary.patients : 0}
        onCancel={() => setResendSummary(null)}
        onConfirm={() => {
          // Pass back the number the operator was shown. The server re-counts
          // and refuses on a mismatch, so a cohort that moved while the dialog
          // was open cannot silently send a different number of messages.
          onResendNotifications(filters, resendSummary.count);
          setResendSummary(null);
        }}
      />
    );

    const projectTableHeaderExtra = (
      <div className="cluster-4">
        {/* <Dropdown
          data-testid="saved-filters-list"
          as={({ onClick }) => {
            return (
              <Button
                type="tertiary"
                className="cluster-2 items-center"
                onClick={onClick}
              >
                <span>Sort by</span>
                <ChevronDownIcon />
              </Button>
            );
          }}
          items={items}
        /> */}
        <Button
          data-testid="add-patient"
          onClick={onAddPatient}
          disabled={isEmpty(createRealm)}
        >
          {t('addRecipients', {
            count: 1,
            context: branding,
          })}
        </Button>
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                data-testid="additional-menu-milestone-add"
                key="milestone-add"
                onClick={onAddMilestone}
              >
                {t('addMilestone')}
              </Menu.Item>
              <Menu.Item
                data-testid="additional-menu-download-csv"
                key="download-csv"
                onClick={onDownloadResponsesCSV}
                disabled={
                  !validate(PROJECT_EXPORT_PARTICIPANTS, {
                    relativeTo: project && project.getDomains(),
                  })
                }
              >
                {t('downloadResponsesCSV')}
              </Menu.Item>
              {project && project.canUseSurveyLinks() && (
                <Menu.Item
                  data-testid="additional-menu-generate-sharable-activity-link"
                  key="generate-sharable-activity-link"
                  onClick={handleOpenSurveyLinksDialog}
                >
                  {t('permissions:project.createSurveyLink')}
                </Menu.Item>
              )}
              <Menu.Item
                data-testid="additional-menu-download-csv-cat-irt"
                key="download-csv-cat-irt"
                disabled
              >
                {t('downloadResponsesCSV')} {t('projects:catIrt')}
              </Menu.Item>
              {map(csvInputSchemas, ({ _id, name }) => {
                return (
                  <Menu.Item
                    key={_id}
                    disabled={!validate(PROJECT_IMPORT_PARTICIPANTS)}
                    data-testid={`additional-menu-upload-csv-${slugify(name)}`}
                    onClick={() => {
                      setInputSchemaId(_id);
                      if (inputRef.current) {
                        inputRef.current.click();
                      }
                    }}
                  >
                    {t('uploadRecipientCSV', {
                      context: branding,
                    })}{' '}
                    {name}
                  </Menu.Item>
                );
              })}
              <Menu.Item
                data-testid="additional-menu-force-sync-to-proms"
                key="force-sync-to-proms"
                // NOTE: The arrow wrapper is required. Menu.Item passes a React
                //       click event to onClick, which would arrive as `filters`
                //       now that the handler takes an argument.
                onClick={() => onForceSyncToProms(filters)}
                disabled={
                  !canForceSync ||
                  !patientsReady ||
                  nPatients === 0 ||
                  isForceSyncOverLimit
                }
                // NOTE: It can be problematic if the sync process is stuck for some reason.
                // disabled={
                //   !progressReady ||
                //   !project ||
                //   project.patientServiceSyncProgress < 100
                // }
                loading
              >
                <MenuItemTooltip title={forceSyncDisabledReason}>
                  {t('syncRecipientService', {
                    context: branding,
                  })}{' '}
                  {project &&
                    progressReady &&
                    project.patientServiceSyncProgress < 100 &&
                    `(${project.patientServiceSyncProgress}%)`}
                </MenuItemTooltip>
              </Menu.Item>
              {/* The two resync actions below share the sync action's cohort and
                  its gate. They act on the Patient App's own tables rather than
                  the provider's projections: each asks it to re-deliver rows it
                  already holds by bumping the auto-increment column the relay
                  polls on. Nothing is created and no payload is rewritten. */}
              <Menu.Item
                data-testid="additional-menu-resync-notifications"
                key="resync-notifications"
                onClick={() => onResyncNotifications(filters)}
                disabled={
                  !canForceSync ||
                  !patientsReady ||
                  nPatients === 0 ||
                  isForceSyncOverLimit
                }
              >
                <MenuItemTooltip title={forceSyncDisabledReason}>
                  {t('resyncNotifications', {
                    context: branding,
                  })}
                </MenuItemTooltip>
              </Menu.Item>
              <Menu.Item
                data-testid="additional-menu-resync-completed"
                key="resync-completed"
                onClick={() => onResyncCompleted(filters)}
                disabled={
                  !canForceSync ||
                  !patientsReady ||
                  nPatients === 0 ||
                  isForceSyncOverLimit
                }
              >
                <MenuItemTooltip title={forceSyncDisabledReason}>
                  {t('resyncCompleted', {
                    context: branding,
                  })}
                </MenuItemTooltip>
              </Menu.Item>
              {/* Divided off deliberately. Everything above this line moves no
                  messages; this sends real SMS/email to patients and cannot be
                  undone, so it must not read as a fourth harmless repair. */}
              <Menu.Divider />
              <Menu.Item
                data-testid="additional-menu-resend-notifications"
                key="resend-notifications"
                onClick={() =>
                  onCountResendCandidates(filters)
                    .then(({ nActivities, nPatients: nResendPatients }) => {
                      // Nothing to send - e.g. every active activity has
                      // already had its reminders. Opening the confirmation
                      // here would be a dead end: "Type 0 to confirm" can never
                      // be confirmed (matchesCount rejects a zero count).
                      if (!nActivities) {
                        setResendSummary(null);
                        notifyError({ useWarning: true })(
                          t('confirmations:patientSync.resend.nothing'),
                        );
                        return;
                      }
                      setResendSummary({
                        count: nActivities,
                        patients: nResendPatients,
                      });
                    })
                    // The handler re-throws after notifying, so that a failed
                    // count never opens a confirmation showing a number it did
                    // not receive. Without this the rejection is unhandled.
                    .catch(() => setResendSummary(null))
                }
                disabled={
                  !canResendNotifications || !patientsReady || nPatients === 0
                }
              >
                <MenuItemTooltip title={resendDisabledReason}>
                  {t('resendReminders', {
                    context: branding,
                  })}
                </MenuItemTooltip>
              </Menu.Item>
              {project && project.blueprintId && (
                <Menu.Item
                  data-testid="additional-menu-edit-project"
                  key="edit-project"
                  onClick={handleEditProject}
                  disabled={
                    !validate(ADMIN_UPDATE_PROJECT, {
                      relativeTo: project && project.getDomains(),
                    })
                  }
                >
                  {t('editProject')}
                </Menu.Item>
              )}
            </Menu>
          }
          trigger={['click']}
        >
          <Button
            data-testid="additional-menu"
            type="tertiary"
            icon={<DotsVerticalIcon />}
          />
        </Dropdown>
      </div>
    );

    return (
      <Stack>
        {project && <ProjectInfo projectId={projectId} />}
        <ConnectedFilters
          storeKey={`dashboards.${dashboardId}`}
          dashboardId={dashboardId}
          projectId={projectId}
          projectMilestones={projectMilestones}
        />
        <Card>
          <ProjectTable
            project={project}
            projectId={projectId}
            patients={patients}
            patientsReady={patientsReady}
            nPatients={nPatients}
            pagination={getPaginationProps()}
            dashboardId={dashboardId}
            paginationKey={paginationKey}
            variables={variables}
            headerExtra={projectTableHeaderExtra}
          />
        </Card>
        {confirmResendDialog}
        <input
          ref={inputRef}
          type="file"
          accept="text/csv"
          onChange={handleFileInputOnChange}
          className="hidden"
        />
      </Stack>
    );
  },
);

ProjectMilestones.propTypes = {
  projectId: PropTypes.string.isRequired,
  dashboardId: PropTypes.string.isRequired,
};

export default ProjectMilestones;
